import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { useGetDomainMetaData } from 'src/use-cases/invoice/useInvoices';
import { useEncodedQueryState } from 'src/hooks/useEncodedQueryState';
import { useEffect, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { IAnalyticStateProps } from './types';
import { ChartViewType } from './sidebar/types';
import { AnalyticsContainerMain } from './Main';
import { LandingTopWidget } from '../landing';
import { LayoutSidebar } from '../sidebar/LayoutSidebar';

const defaultFiltersState: IAnalyticStateProps = {
  name: 'New Report',
  description: 'Report Description',
  filters: {
    selectedChartView: ChartViewType.TREND,
    formValues: {
      granularity: 'DAILY',
      dateReference: 'PICKUP_DATE',
      currencyReference: undefined,
      filters: {
        dateFilters: {
          years: [new Date().getFullYear().toString()],
        },
        tradeLaneFilters: {},
        shipmentFilters: {},
      },
      invoiceStatusSet: ['APPROVED'],
    } as AnalyticSearchRequestDto,
  },
};

const AnalyticsContainer = () => {
  const [hashedFilter, setHashedFilter] = useEncodedQueryState(defaultFiltersState, 'report');
  const { data: dataStatus, isLoading } = useGetDomainMetaData();

  // Get global currency from domain metadata
  const globalCurrency = dataStatus?.defaultCurrency;

  // Create a minimal filter request to fetch currencies and available years
  const minimalFilterRequest = useMemo(
    () => ({
      currencyReference: hashedFilter.filters.formValues.currencyReference || globalCurrency,
      dateReference: hashedFilter.filters.formValues.dateReference || 'PICKUP_DATE',
    }),
    [hashedFilter.filters.formValues.currencyReference, hashedFilter.filters.formValues.dateReference, globalCurrency]
  );

  // Get the default currency - use global currency or fallback to USD
  // FINDEV-5193: Simplified to not depend on filterMetadata which is now deferred
  const defaultCurrency = useMemo(() => {
    // Use global currency from domain metadata, or fallback to USD
    return globalCurrency || 'USD';
  }, [globalCurrency]);

  // Get the default year - use current year as default
  // FINDEV-5193: Simplified to not depend on filterMetadata which is now deferred
  const defaultYear = useMemo(() => {
    return new Date().getFullYear().toString();
  }, []);

  useEffect(() => {
    const currentCurrency = hashedFilter.filters.formValues.currencyReference;
    const currentYear = hashedFilter.filters.formValues.filters?.dateFilters?.years?.[0];

    // Check if we need to update currency or year
    const needsCurrencyUpdate = defaultCurrency && !currentCurrency;
    // Update year if no year is set
    const needsYearUpdate = defaultYear && !currentYear;

    // Only update if currency or year needs to be set
    if (needsCurrencyUpdate || needsYearUpdate) {
      setHashedFilter({
        name: hashedFilter.name,
        description: hashedFilter.description,
        filters: {
          selectedChartView: hashedFilter.filters.selectedChartView,
          formValues: {
            ...hashedFilter.filters.formValues,
            ...(needsCurrencyUpdate ? { currencyReference: defaultCurrency } : {}),
            ...(needsYearUpdate
              ? {
                  filters: {
                    ...hashedFilter.filters.formValues.filters,
                    dateFilters: {
                      ...hashedFilter.filters.formValues.filters?.dateFilters,
                      years: [defaultYear],
                    },
                  },
                }
              : {}),
          },
        },
      });
    }
  }, [defaultCurrency, defaultYear, hashedFilter, setHashedFilter]);

  // Show layout with loading spinner inside content area while loading
  if (isLoading || !hashedFilter.filters.formValues?.currencyReference) {
    return (
      <div className='w-full h-full relative'>
        <LandingTopWidget title='Reports & Analytics' />
        <LayoutSidebar>
          <div className='flex-1 flex items-center justify-center min-h-[400px] bg-neutral-10'>
            <CircularProgress color='primary' />
          </div>
        </LayoutSidebar>
      </div>
    );
  }

  return <AnalyticsContainerMain hashedFilter={hashedFilter} setHashedFilter={setHashedFilter} globalCurrency={globalCurrency} />;
};
export default AnalyticsContainer;
